import type { MonthCell, Task } from "@app/core";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useMobileUx } from "../../lib/useMobileUx.js";
import { useAppLocale } from "../../lib/useAppLocale.js";
import { useTaskDialogStore } from "../../stores/index.js";
import { CalendarRenderIndexProvider } from "./CalendarRenderIndex.js";
import { CalendarBackgroundMenu } from "./CalendarBackgroundMenu.js";
import { isCalendarBackgroundCreateSuppressed } from "./calendar-background-create-guard.js";
import { MonthDayCell } from "./MonthDayCell.js";
import { isSameYMD } from "./drag.js";
import { computeAllDayRows } from "./month-helpers.js";
import { useMonthDrag } from "./useMonthDrag.js";
import { useToday } from "./useNowMinute.js";

function sameRowMap(a: Map<string, number>, b: Map<string, number>): boolean {
  if (a.size !== b.size) return false;
  for (const [k, v] of b) if (a.get(k) !== v) return false;
  return true;
}

export function MonthGrid({ anchor, cells, onMonthShift }: { anchor: Date; cells: MonthCell[]; onMonthShift?: (delta: number) => void }) {
  const today = useToday();
  const mobile = useMobileUx();
  const locale = useAppLocale();
  // Mobile: a horizontal swipe pages months; a tap on an empty cell opens the
  // quick-create sheet (the all-day drag-create is mouse-only, disabled here).
  const swipeRef = useRef<{ x: number; y: number } | null>(null);
  function onBodyTouchStart(e: React.TouchEvent) {
    if (!mobile) return;
    const t = e.touches[0];
    swipeRef.current = t ? { x: t.clientX, y: t.clientY } : null;
  }
  function onBodyTouchEnd(e: React.TouchEvent) {
    if (!mobile || !swipeRef.current) return;
    const s = swipeRef.current; swipeRef.current = null;
    const t = e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - s.x, dy = t.clientY - s.y;
    if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.4) { onMonthShift?.(dx < 0 ? 1 : -1); return; }
    if (Math.abs(dx) < 12 && Math.abs(dy) < 12) {
      const target = e.target as HTMLElement;
      if (target.closest("[data-chip]")) return; // tapped a task → its own handler edits it
      const cellEl = target.closest<HTMLElement>("[data-day-iso]");
      const iso = cellEl?.dataset["dayIso"];
      if (isCalendarBackgroundCreateSuppressed() || useTaskDialogStore.getState().open) return;
      if (iso) { const d = new Date(iso); d.setHours(9, 0, 0, 0); useTaskDialogStore.getState().openCreate(d); }
    }
  }
  // Derive the weekday header from the first rendered week so it follows the
  // user's week-start (Sunday vs Monday) and locale — a hardcoded Mon–Sun row
  // mismatched the columns when weekStartsOn = 0 (Sunday).
  const dayHeaders = cells.slice(0, 7).map((c) => ({
    key: c.date.getDay(),
    label: c.date.toLocaleDateString(locale, { weekday: "short" }),
  }));
  const openEdit = useTaskDialogStore((s) => s.openEdit);
  const openCreate = useTaskDialogStore((s) => s.openCreate);
  const [dropTargetIso, setDropTargetIso] = useState<string | null>(null);
  const [bgCtx, setBgCtx] = useState<{ x: number; y: number; date: Date } | null>(null);
  // Cell whose hidden overflow tasks are currently expanded (via "+N more").
  const [expandedIso, setExpandedIso] = useState<string | null>(null);

  const { isDragHighlighted, handleDrop, startCreate, onCellEnter, startMove, startResizeLeft, startResizeRight } = useMonthDrag();

  // Dynamic chip capacity: cells stretch with `auto-rows-fr`, so a taller window
  // fits more rows. Measure REAL DOM — a cell's available height below its date
  // header and an actual chip-row height — rather than hardcoded px, so the cell
  // always packs to fill the window (all-day rows + timed + "+N more" share this
  // budget). A `+1` overshoot would overflow → an extra timed row is held back as
  // "+N more"; the visible rows always fit.
  const bodyRef = useRef<HTMLDivElement>(null);
  const [rowCapacity, setRowCapacity] = useState(3);
  const weekCount = Math.ceil(cells.length / 7);
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    const measure = () => {
      const cell = el.firstElementChild as HTMLElement | null;
      const rows = cell?.querySelector<HTMLElement>("[data-cell-rows]");
      if (!cell || !rows) return;
      // Cell height from the body's ALLOCATED size (flex-1), not the cell's own
      // bounding box — a cell grows with its content, which would feed back into
      // the measurement and oscillate the capacity.
      const cellH = (el.clientHeight - (weekCount - 1)) / weekCount; // minus gap-px rows
      // Overhead (date badge + margins + bottom padding) is content-independent.
      const padBottom = parseFloat(getComputedStyle(cell).paddingBottom) || 0;
      const overhead = rows.getBoundingClientRect().top - cell.getBoundingClientRect().top + padBottom;
      const available = cellH - overhead;
      const sample = rows.firstElementChild as HTMLElement | null;
      const rowH = sample?.offsetHeight || 20; // h-5 fallback
      const gap = parseFloat(getComputedStyle(rows).rowGap) || 0;
      const fit = Math.floor((available + gap) / (rowH + gap));
      setRowCapacity(Math.max(1, fit));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [weekCount, cells]);

  // Per-week greedy row assignment so multi-day tasks line up across cells.
  // Unchanged weeks keep their previous Map reference so the memoized
  // `MonthDayCell`s in them skip re-rendering after an edit elsewhere.
  const prevMaps = useRef<Map<string, number>[]>([]);
  const { weekRowMaps, weekMaxRows } = useMemo(() => {
    const weekCount = Math.ceil(cells.length / 7);
    const maps: Map<string, number>[] = [];
    const maxRows: number[] = [];
    for (let w = 0; w < weekCount; w++) {
      const weekCells = cells.slice(w * 7, (w + 1) * 7);
      const seenIds = new Set<string>();
      const weekTasks: Task[] = [];
      for (const cell of weekCells) {
        for (const t of cell.tasks.filter((t2) => t2.all_day)) {
          if (!seenIds.has(t.id)) { seenIds.add(t.id); weekTasks.push(t); }
        }
      }
      const rMap = computeAllDayRows(weekTasks);
      const prev = prevMaps.current[w];
      const kept = prev && sameRowMap(prev, rMap) ? prev : rMap;
      maps.push(kept);
      maxRows.push(kept.size > 0 ? Math.max(...kept.values()) : -1);
    }
    prevMaps.current = maps;
    return { weekRowMaps: maps, weekMaxRows: maxRows };
  }, [cells]);

  return (
    <CalendarRenderIndexProvider>
    <div className="flex min-h-0 flex-1 select-none flex-col">
      <div className="grid shrink-0 grid-cols-7 border-b text-xs text-muted-foreground">
        {dayHeaders.map((d) => <div key={d.key} className="px-2 py-1">{d.label}</div>)}
      </div>
      {/* `minmax(0,1fr)` + `min-h-0`: week rows split the pane height EXACTLY and
          shrink below content when short, so the grid always fits the window
          (no page scrollbar). Cells clip overflow; the dynamic `rowCapacity`
          keeps chip counts within the shrunk cells, rolling the rest into
          "+N more". */}
      <div
        ref={bodyRef}
        className="grid min-h-0 flex-1 auto-rows-[minmax(0,1fr)] grid-cols-7 gap-px bg-border"
        onTouchStart={onBodyTouchStart}
        onTouchEnd={onBodyTouchEnd}
      >
        {cells.map((c, cellIdx) => {
          const iso = c.date.toISOString();
          const weekIdx = Math.floor(cellIdx / 7);
          return (
            <MonthDayCell
              key={iso}
              cell={c}
              isToday={isSameYMD(c.date, today)}
              isHighlighted={isDragHighlighted(c.date)}
              isDropTarget={dropTargetIso === iso}
              isExpanded={expandedIso === iso}
              rowMap={weekRowMaps[weekIdx]!}
              weekMaxRow={weekMaxRows[weekIdx]!}
              rowCapacity={rowCapacity}
              onCellEnter={onCellEnter}
              onDragOverCell={setDropTargetIso}
              onDragLeaveCell={(x) => setDropTargetIso((cur) => (cur === x ? null : cur))}
              onDropCell={(e, date) => { setDropTargetIso(null); void handleDrop(e, date); }}
              onContextMenuCell={(e, date) => setBgCtx({ x: e.clientX, y: e.clientY, date })}
              onMouseDownCell={mobile ? () => {} : (e, date) => startCreate(e, date)}
              onEditTask={openEdit}
              onMoveStart={startMove}
              onResizeLeft={startResizeLeft}
              onResizeRight={startResizeRight}
              onSetExpanded={setExpandedIso}
            />
          );
        })}
      </div>
      <span className="sr-only">{anchor.toISOString()}</span>
      {bgCtx && (
        <CalendarBackgroundMenu
          x={bgCtx.x}
          y={bgCtx.y}
          onClose={() => setBgCtx(null)}
          onCreate={() => openCreate(bgCtx.date)}
        />
      )}
    </div>
    </CalendarRenderIndexProvider>
  );
}
